import type {
  ApiResponse,
  CreateInvitationResponse,
  CreateProgramInvitationRequest,
} from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

type Input = CreateProgramInvitationRequest & {
  programId: string;
};

/**
 * UPFRONT program-invitation creation for an email-only recipient (no creator
 * profile): `POST /dashboard/programs/:programId/invitations`. Returns the same
 * `CreateInvitationResponse` shape as the creator path (`socialProfileId` is
 * null at runtime here), so the compose step gets a real `invitationId` (the
 * outreach send requires it) and a real `invitationLink` (the universal floor).
 */
const createProgramInvitationByEmailFn = async (
  params: Input
): Promise<CreateInvitationResponse> => {
  const { programId, ...payload } = params;
  const response = await api.post<ApiResponse<CreateInvitationResponse>>(
    `/dashboard/programs/${programId}/invitations`,
    payload
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }

  throw new Error(response.data.message || 'Failed to create program invitation');
};

export const useCreateProgramInvitationByEmail = () => {
  // No hook-level toast: the caller surfaces the backend message (e.g. a
  // suppressed recipient) with its own context.
  return useMutation<CreateInvitationResponse, Error, Input>({
    mutationFn: createProgramInvitationByEmailFn,
  });
};
